<script lang="ts" setup>
import { ref, inject, watch, computed } from 'vue';

import type { Ref } from 'vue';
import type { Viewer, Nullable, Extension } from '@immersive-tech/viewer-engine';
import type { Perspectives } from '@/viewerExtensions';

interface IProps {
  angleRestriction: number | '';
}

interface IEmits {
  (e: 'change', value: Nullable<number>): unknown;
}

const emit = defineEmits<IEmits>();
const props = defineProps<IProps>();

const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const previewRestrictions = inject('previewRestrictions') as Ref<boolean>;
const angleRestriction = ref(props.angleRestriction);
const perspectivesExtension = viewer.value?.extensions.get('Perspectives') as Extension<Perspectives>;

const parsedAngleRestriction = computed<Nullable<number>>(() => {
  if (angleRestriction.value === '') return null;

  return angleRestriction.value;
});

const angleRestrictionWatcher = () => {
  if (previewRestrictions.value === false || parsedAngleRestriction.value === null) {
    perspectivesExtension.resetAngleRestriction();
  } else {
    perspectivesExtension.setAngleRestriction(parsedAngleRestriction.value);
  }

  emit('change', parsedAngleRestriction.value);
};

const previewRestrictionsWatcher = (newValue: boolean) => {
  if (newValue) {
    perspectivesExtension.setBaseRotation();
    if (parsedAngleRestriction.value === null) {
      perspectivesExtension.resetAngleRestriction();
    } else {
      perspectivesExtension.setAngleRestriction(parsedAngleRestriction.value);
    }
  } else {
    perspectivesExtension.resetBaseRotation();
    perspectivesExtension.resetAngleRestriction();
  }
};

watch(angleRestriction, angleRestrictionWatcher);
watch(previewRestrictions, previewRestrictionsWatcher);
</script>

<template>
  <input
    id="perspective-angle"
    v-model="angleRestriction"
    class="border text-xs p-1"
    type="number"
    min="0"
    max="360"
    step="0.01"
  >
</template>
